import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppUploadFile from '@nxsys/components/AppUploadFile';
import { AppDispatch, AppState } from '@nxsys/store';
import { aepnActions } from '@nxsys/store/slices/aepn/reducer/aepnSlice';
import { appActions } from '@nxsys/store/slices/app/reducer/appSlice';
import { showToast } from '@nxsys/utils/toast';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

type UploadAEPNModalProps = {
  isOpen: boolean;
  toggle: () => void;
  onUploadSuccess: (data: any[]) => void;
};

const UploadAEPNModal: React.FC<UploadAEPNModalProps> = ({
  isOpen,
  toggle,
  onUploadSuccess,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { actionLoading } = useSelector((state: AppState) => state.aepnStore);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const handleFileChange = (file: File) => {
    if (!file.name.endsWith('.json')) {
      showToast({ type: 'warning', message: 'Only .json files are accepted.' });
      return;
    }
    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      showToast({ type: 'warning', message: 'Please select a file to upload.' });
      return;
    }

    try {
      const uploadResponse = await dispatch(
        appActions.uploadFile({ file: selectedFile }),
      ).unwrap();
      const fileUrl = (uploadResponse as any)?.url?.split('?')[0] ?? '';

      const res = await dispatch(
        aepnActions.aepnUpload({
          url: fileUrl,
          fileName: selectedFile.name,
          fileSize: selectedFile.size,
        }),
      ).unwrap();
      const data: any[] = Array.isArray(res?.data) ? res.data : res?.data ? [res.data] : [];
      setSelectedFile(null);
      toggle();
      onUploadSuccess(data);
    } catch (err: any) {
      const msg = err?.message || err?.response?.data?.message || 'Upload failed';
      showToast({ type: 'error', message: msg });
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    toggle();
  };

  return (
    <AppModalContainer
      title="Upload AEPN File"
      isOpen={isOpen}
      toggle={handleClose}
      width={560}
      actions={[
        {
          name: 'Cancel',
          variant: 'outlined',
          onClick: handleClose,
          disabled: actionLoading,
        },
        {
          name: 'Upload',
          buttonType: 'primary',
          onClick: handleUpload,
          loading: actionLoading,
          disabled: !selectedFile || actionLoading,
        },
      ]}
    >
      <div className="py-4">
        <p className="mb-4 text-gray-600 text-sm">
          Drag and drop your JSON file here, or click to browse. Only <strong>.json</strong> files are accepted.
        </p>
        <AppUploadFile
          onChange={handleFileChange}
          maxFileSize={10}
        />
      </div>
    </AppModalContainer>
  );
};

export default UploadAEPNModal;
